import { useEffect, useState } from 'react';
import {
  Drawer,
  Descriptions,
  Tag,
  Typography,
  Space,
  Button,
  Select,
  Divider,
  List,
  Collapse,
  Alert,
  App,
  Skeleton,
  Empty,
  Timeline,
} from 'antd';
import {
  ReloadOutlined,
  DownloadOutlined,
  LinkOutlined,
  MailOutlined,
  MessageOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import useIncidentApi from 'hooks/useIncidentApi';
import ReplyIncidentModal from './ReplyIncidentModal';

const STATUS_LABEL = {
  open: { label: 'Abierto', color: 'blue' },
  in_progress: { label: 'En progreso', color: 'gold' },
  resolved: { label: 'Resuelto', color: 'green' },
  discarded: { label: 'Descartado', color: 'default' },
};

const EMAIL_STATUS_LABEL = {
  pending: { label: 'Pendiente', color: 'default' },
  sent: { label: 'Enviado', color: 'green' },
  failed: { label: 'Falló', color: 'red' },
};

const CATEGORY_LABEL = {
  bug: { label: 'Bug', color: 'red' },
  suggestion: { label: 'Sugerencia', color: 'green' },
  question: { label: 'Pregunta', color: 'blue' },
  other: { label: 'Otro', color: 'default' },
};

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const REPLY_EMAIL_STATUS_LABEL = {
  pending: { label: 'Pendiente', color: 'default' },
  sent: { label: 'Email enviado', color: 'green' },
  failed: { label: 'Email falló', color: 'red' },
  skipped: { label: 'Sin email', color: 'default' },
};

const IncidentDetailDrawer = ({ open, incidentId, onClose, onUpdated }) => {
  const { message } = App.useApp();
  const { detail, updateStatus, retryEmail, reply, retryReplyEmail } = useIncidentApi();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryingReplyId, setRetryingReplyId] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!open || !incidentId) {
      setData(null);
      return undefined;
    }
    (async () => {
      setLoading(true);
      try {
        const result = await detail(incidentId);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) message.error('No se pudo cargar el detalle.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, incidentId, detail, message]);

  const handleStatusChange = async (newStatus) => {
    if (!data?.incident) return;
    setSaving(true);
    try {
      const updated = await updateStatus(data.incident.id, newStatus);
      setData((prev) => (prev ? { ...prev, incident: updated } : prev));
      message.success('Estado actualizado.');
      onUpdated?.();
    } catch (err) {
      message.error('No se pudo actualizar el estado.');
    } finally {
      setSaving(false);
    }
  };

  const handleReplySubmit = async (payload) => {
    if (!data?.incident) return;
    setReplySubmitting(true);
    try {
      const result = await reply(data.incident.id, payload);
      // Refresh: detail ahora incluye la nueva reply y el status quizá cambió
      const fresh = await detail(data.incident.id);
      setData(fresh);
      const replyStatus = result?.reply?.emailStatus;
      if (replyStatus === 'sent') {
        message.success('Respuesta enviada y notificada al reportero.');
      } else if (replyStatus === 'failed') {
        message.warning(
          `Respuesta guardada pero el email falló: ${result.reply.emailError ?? 'sin detalle'}`
        );
      } else {
        message.success('Respuesta guardada.');
      }
      setReplyOpen(false);
      onUpdated?.();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || 'Error al enviar la respuesta';
      if (status === 400) {
        message.error(msg);
      } else {
        message.error('No se pudo enviar la respuesta.');
      }
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleRetryEmail = async () => {
    if (!data?.incident) return;
    setRetrying(true);
    try {
      const updated = await retryEmail(data.incident.id);
      setData((prev) => (prev ? { ...prev, incident: updated } : prev));
      if (updated.emailStatus === 'sent') {
        message.success('Email reenviado correctamente.');
      } else if (updated.emailStatus === 'failed') {
        message.warning(`Reintento falló: ${updated.emailError ?? 'sin detalle'}`);
      }
      onUpdated?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        message.info('Esta incidencia ya tiene el email enviado.');
      } else {
        message.error('No se pudo reintentar el email.');
      }
    } finally {
      setRetrying(false);
    }
  };

  const handleRetryReply = async (replyId) => {
    if (!data?.incident) return;
    setRetryingReplyId(replyId);
    try {
      const result = await retryReplyEmail(data.incident.id, replyId);
      // Refresh: la reply cambió de emailStatus.
      const fresh = await detail(data.incident.id);
      setData(fresh);
      const st = result?.reply?.emailStatus;
      if (st === 'sent') {
        message.success('Email de la respuesta reenviado.');
      } else if (st === 'failed') {
        message.warning(`Reintento falló: ${result.reply.emailError ?? 'sin detalle'}`);
      }
      onUpdated?.();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || 'No se pudo reintentar el email.';
      if (status === 400) {
        message.info(msg);
      } else {
        message.error('No se pudo reintentar el email de la respuesta.');
      }
    } finally {
      setRetryingReplyId(null);
    }
  };

  const incident = data?.incident;
  const attachments = data?.attachments ?? [];
  const replies = data?.replies ?? [];
  const reporter = data?.reporter ?? null;
  const metadata = incident?.metadata ?? null;
  const capturedErrors = Array.isArray(metadata?.errors) ? metadata.errors : [];

  return (
    <Drawer
      title={incident ? `Incidencia #${incident.id}` : 'Incidencia'}
      placement="right"
      width={640}
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      {loading && <Skeleton active paragraph={{ rows: 6 }} />}

      {!loading && !incident && <Empty description="No se encontró la incidencia" />}

      {!loading && incident && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap>
            <Tag color={CATEGORY_LABEL[incident.category]?.color ?? 'default'}>
              {CATEGORY_LABEL[incident.category]?.label ?? incident.category}
            </Tag>
            <Tag color={STATUS_LABEL[incident.status]?.color ?? 'default'}>
              {STATUS_LABEL[incident.status]?.label ?? incident.status}
            </Tag>
            <Tag color={EMAIL_STATUS_LABEL[incident.emailStatus]?.color ?? 'default'}>
              <MailOutlined />{' '}
              {EMAIL_STATUS_LABEL[incident.emailStatus]?.label ?? incident.emailStatus}
            </Tag>
          </Space>

          <Typography.Title level={4} style={{ margin: 0 }}>
            {incident.subject}
          </Typography.Title>

          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>
            {incident.description}
          </Typography.Paragraph>

          <Space wrap>
            <Typography.Text>Estado:</Typography.Text>
            <Select
              value={incident.status}
              onChange={handleStatusChange}
              loading={saving}
              disabled={saving}
              style={{ width: 180 }}
              options={Object.entries(STATUS_LABEL).map(([value, v]) => ({
                value,
                label: v.label,
              }))}
            />
            <Button type="primary" icon={<MessageOutlined />} onClick={() => setReplyOpen(true)}>
              Responder
            </Button>
            {incident.emailStatus !== 'sent' && (
              <Button icon={<ReloadOutlined />} onClick={handleRetryEmail} loading={retrying}>
                Reintentar email
              </Button>
            )}
          </Space>

          {incident.emailStatus === 'failed' && incident.emailError && (
            <Alert
              type="error"
              showIcon
              message="Último intento de email falló"
              description={incident.emailError}
            />
          )}

          <Divider style={{ margin: '12px 0' }} />

          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Reportado">
              {dayjs(incident.createdAt).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
            <Descriptions.Item label="Tenant">{incident.tenantId ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Usuario (ID)">{incident.userId ?? '—'}</Descriptions.Item>
            {incident.url && (
              <Descriptions.Item label="URL origen">
                <Typography.Link href={incident.url} target="_blank">
                  <LinkOutlined /> {incident.url}
                </Typography.Link>
              </Descriptions.Item>
            )}
            {incident.userAgent && (
              <Descriptions.Item label="User Agent">
                <Typography.Text style={{ fontSize: 12 }}>{incident.userAgent}</Typography.Text>
              </Descriptions.Item>
            )}
            {metadata?.route && (
              <Descriptions.Item label="Ruta in-app">
                <Typography.Text code>{metadata.route}</Typography.Text>
              </Descriptions.Item>
            )}
            {metadata?.screen && (
              <Descriptions.Item label="Pantalla">
                {metadata.screen.w} × {metadata.screen.h}
              </Descriptions.Item>
            )}
          </Descriptions>

          {attachments.length > 0 && (
            <>
              <Divider orientation="left" style={{ margin: '4px 0' }}>
                Adjuntos ({attachments.length})
              </Divider>
              <List
                size="small"
                bordered
                dataSource={attachments}
                renderItem={(att) => (
                  <List.Item
                    actions={[
                      att.signedUrl ? (
                        <Typography.Link
                          key="dl"
                          href={att.signedUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <DownloadOutlined /> Descargar
                        </Typography.Link>
                      ) : (
                        <Typography.Text key="dl" type="secondary">
                          URL no disponible
                        </Typography.Text>
                      ),
                    ]}
                  >
                    <Space>
                      <Typography.Text>{att.filename}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {att.file_type} — {formatBytes(att.file_size)}
                      </Typography.Text>
                    </Space>
                  </List.Item>
                )}
              />
            </>
          )}

          {capturedErrors.length > 0 && (
            <Collapse
              size="small"
              items={[
                {
                  key: 'errors',
                  label: `Errores JS capturados (${capturedErrors.length})`,
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {capturedErrors.map((err, idx) => (
                        <div
                          key={err.id ?? idx}
                          style={{
                            border: '1px solid #eee',
                            borderRadius: 6,
                            padding: 8,
                            background: '#fafafa',
                          }}
                        >
                          <Typography.Text strong style={{ fontSize: 12 }}>
                            [{err.type}] {err.message}
                          </Typography.Text>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                            {err.timestamp} — {err.url}
                          </div>
                          {err.stack && (
                            <pre
                              style={{
                                fontSize: 10,
                                background: '#f5f5f5',
                                padding: 6,
                                borderRadius: 4,
                                maxHeight: 180,
                                overflow: 'auto',
                                whiteSpace: 'pre-wrap',
                                marginTop: 6,
                              }}
                            >
                              {err.stack}
                            </pre>
                          )}
                        </div>
                      ))}
                    </Space>
                  ),
                },
              ]}
            />
          )}

          <Divider orientation="left" style={{ margin: '4px 0' }}>
            Conversación
          </Divider>
          <Timeline
            items={[
              {
                color: 'gray',
                dot: <UserOutlined />,
                children: (
                  <>
                    <Typography.Text strong>{reporter?.fullname || 'Reportero'}</Typography.Text>{' '}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(incident.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Typography.Text>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
                      {incident.description}
                    </div>
                  </>
                ),
              },
              ...replies.map((r) => ({
                color: 'blue',
                dot: <MessageOutlined />,
                children: (
                  <>
                    <Space size="small">
                      <Typography.Text strong>Equipo de soporte</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {dayjs(r.createdAt).format('DD/MM/YYYY HH:mm')}
                      </Typography.Text>
                      <Tag color={REPLY_EMAIL_STATUS_LABEL[r.emailStatus]?.color ?? 'default'}>
                        {REPLY_EMAIL_STATUS_LABEL[r.emailStatus]?.label ?? r.emailStatus}
                      </Tag>
                    </Space>
                    <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{r.message}</div>
                    {r.emailedTo && (
                      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                        Enviado a {r.emailedTo}
                      </Typography.Text>
                    )}
                    {r.emailStatus === 'failed' && r.emailError && (
                      <Alert
                        type="error"
                        showIcon
                        style={{ marginTop: 6 }}
                        message={r.emailError}
                      />
                    )}
                    {r.emailStatus === 'failed' && (
                      <div style={{ marginTop: 6 }}>
                        <Button
                          size="small"
                          icon={<ReloadOutlined />}
                          loading={retryingReplyId === r.id}
                          onClick={() => handleRetryReply(r.id)}
                        >
                          Reintentar email
                        </Button>
                      </div>
                    )}
                  </>
                ),
              })),
            ]}
          />
        </Space>
      )}
      <ReplyIncidentModal
        open={replyOpen}
        incident={incident}
        reporter={reporter}
        submitting={replySubmitting}
        onCancel={() => (replySubmitting ? null : setReplyOpen(false))}
        onSubmit={handleReplySubmit}
      />
    </Drawer>
  );
};

export default IncidentDetailDrawer;
